import React, { useMemo } from "react";

import styles from "../styles/styles.module.css";
import { useChartContext } from "../core/ChartContext";
import {
  CANDLESTICK_COLORS,
  generateAreaPath,
  generateLinePath,
} from "~utils/ticker-chart";
import { useChartSubscription, useStoreRef } from "~hooks/ticker-chart";

export interface MultiColorLineProps {
  readonly seriesId: string;
  readonly strokeWidth?: number;
  readonly showGradient?: boolean;
}

const MultiColorLineInner: React.FC<MultiColorLineProps> = ({
  seriesId,
  strokeWidth = 2,
  showGradient = false,
}) => {
  const store = useChartContext();
  const storeRef = useStoreRef(store);

  const revision = useChartSubscription(store, ["data", "scales"]);

  return useMemo(() => {
    const renderableData = storeRef.current.getRenderableData(seriesId);
    const visibleData = storeRef.current.getVisibleData(seriesId);
    const scales = storeRef.current.getScales();
    const layout = storeRef.current.getLayout();
    const state = storeRef.current.getState();
    const seriesMeta = state.seriesMeta.get(seriesId);

    if (renderableData.length === 0) return null;

    const baselineY = scales.y.scale(state.baseValue);

    // Generate the full line path once
    const pathD = generateLinePath(renderableData, {
      xScale: scales.x,
      yScale: scales.y,
    });

    const priceArea = layout.priceArea;
    const clipIdAbove = `multicolor-clip-above-${seriesId}`;
    const clipIdBelow = `multicolor-clip-below-${seriesId}`;

    // Generate gradient fills if enabled
    let gradientContent: React.ReactNode = null;

    if (showGradient) {
      const areaPathD = generateAreaPath(renderableData, {
        xScale: scales.x,
        yScale: scales.y,
        baselineY,
      });

      const gradientIdAbove = `multicolor-gradient-above-${seriesId}`;
      const gradientIdBelow = `multicolor-gradient-below-${seriesId}`;

      gradientContent = (
        <>
          <defs>
            <linearGradient
              id={gradientIdAbove}
              x1={0}
              y1={priceArea.y}
              x2={0}
              y2={baselineY}
              gradientUnits="userSpaceOnUse"
            >
              <stop
                offset="0%"
                stopColor={CANDLESTICK_COLORS.up}
                stopOpacity={0.3}
              />
              <stop
                offset="100%"
                stopColor={CANDLESTICK_COLORS.up}
                stopOpacity={0}
              />
            </linearGradient>
            <linearGradient
              id={gradientIdBelow}
              x1={0}
              y1={baselineY}
              x2={0}
              y2={priceArea.y + priceArea.height}
              gradientUnits="userSpaceOnUse"
            >
              <stop
                offset="0%"
                stopColor={CANDLESTICK_COLORS.down}
                stopOpacity={0}
              />
              <stop
                offset="100%"
                stopColor={CANDLESTICK_COLORS.down}
                stopOpacity={0.3}
              />
            </linearGradient>
          </defs>
          <g clipPath={`url(#${clipIdAbove})`}>
            <path
              className={styles.area}
              d={areaPathD}
              fill={`url(#${gradientIdAbove})`}
              stroke="none"
              data-component="price-movement-chart-multicolor-gradient"
            />
          </g>
          <g clipPath={`url(#${clipIdBelow})`}>
            <path
              className={styles.area}
              d={areaPathD}
              fill={`url(#${gradientIdBelow})`}
              stroke="none"
              data-component="price-movement-chart-multicolor-gradient"
            />
          </g>
        </>
      );
    }

    return (
      <>
        <defs>
          <clipPath id={clipIdAbove}>
            <rect
              x={priceArea.x}
              y={priceArea.y}
              width={priceArea.width}
              height={Math.max(0, baselineY - priceArea.y)}
            />
          </clipPath>
          <clipPath id={clipIdBelow}>
            <rect
              x={priceArea.x}
              y={baselineY}
              width={priceArea.width}
              height={Math.max(0, priceArea.y + priceArea.height - baselineY)}
            />
          </clipPath>
        </defs>

        {gradientContent}

        {/* Above baseline: green */}
        <g
          clipPath={`url(#${clipIdAbove})`}
          data-component="price-movement-chart-multicolor-above"
        >
          <path
            className={styles.line}
            d={pathD}
            stroke={CANDLESTICK_COLORS.up}
            strokeWidth={strokeWidth}
            fill="none"
            vectorEffect="non-scaling-stroke"
          />
        </g>

        {/* Below baseline: red */}
        <g
          clipPath={`url(#${clipIdBelow})`}
          data-component="price-movement-chart-multicolor-below"
        >
          <path
            className={styles.line}
            d={pathD}
            stroke={CANDLESTICK_COLORS.down}
            strokeWidth={strokeWidth}
            fill="none"
            vectorEffect="non-scaling-stroke"
          />
        </g>
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision, seriesId, strokeWidth, showGradient]);
};

export const MultiColorLine = React.memo(MultiColorLineInner);

MultiColorLine.displayName = "MultiColorLine";
